'use client';

import { type ChangeEvent } from 'react';
import { Close, Search } from '@mui/icons-material';
import { alpha, Box, IconButton, InputAdornment, TextField } from '@mui/material';

import { useT } from '@/hooks';
import { colors } from '@/styles/theme';

interface SearchPageInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const SearchPageInput = ({ value, onChange }: SearchPageInputProps) => {
  const { t } = useT();
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');
  };

  return (
    <Box sx={{ mb: 3 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={t('common.searchPorvenix')}
        value={value}
        onChange={handleSearchChange}
        autoFocus
        // The browser's own input-history suggestions (not our in-app results)
        // paint a native autofill background when a previous entry is picked —
        // it ignores our theme entirely, showing as a stray gray/blue block.
        // Turn native suggestions off and neutralize -webkit-autofill as a
        // fallback for browsers that still apply it despite autoComplete="off".
        autoComplete="off"
        inputProps={{ autoComplete: 'off' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? colors.neutral[900] : colors.neutral[50],
            borderRadius: '10px',
            height: '42px',
            fontSize: '0.9375rem',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? colors.neutral[800] : colors.neutral[200],
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? colors.neutral[800] : colors.neutral[100],
              borderColor: (theme) =>
                theme.palette.mode === 'dark' ? colors.neutral[700] : colors.neutral[300],
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? colors.bg.secondary.dark
                  : colors.bg.secondary.light,
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-input': {
              color: 'text.primary',
              '&::placeholder': {
                color: 'text.secondary',
                opacity: 0.8,
              },
            },
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px transparent inset',
            WebkitTextFillColor: 'inherit',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        }}
      />
    </Box>
  );
};
